import {
  AfterContentInit,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import {
  getValue,
  isDefined,
  isDefinedAndFilledArray,
  isDefinedAndFilledString,
  SOVerify,
} from '../../../../shared/utility/verify.lib';
import { SubscriptionComponent } from '../../../../shared/subscription/subscription.component';
import { Store } from '@ngrx/store';
import {
  availableConfiguration,
  defaultConfiguration,
  documentSession,
  savedConfiguration,
  selectedDocumentTypes,
  selectedLanguages,
  selectedProducts,
  sessionLanguage,
} from '../../../../redux/mydoc.selector';
import { IExportConfiguration } from '../../../../models/export/export-configuration.model';
import { takeUntil } from 'rxjs';
import { IExportStructure } from '../../../../models/export/export-structure.model';
import { IExportText } from '../../../../models/export/export-text.model';
import { ExportSettingType } from '../../enum/export-setting-type.enum';
import { IDocumentSession } from '../../../../models/session/document-session.model';
import { IDocumentType } from '../../../../models/document/document-type.model';
import { IProductItem } from '../../../../models/item/product-item.model';
import { ILanguageText } from '../../../../models/language/language-text.model';
import { TrackingHelperService } from '../../services/tracking-helper/tracking-helper.service';

@Component({
  selector: 'app-file-structure',
  templateUrl: './base-structure.component.html',
  styleUrl: './base-structure.component.scss',
})
export class BaseStructureComponent
  extends SubscriptionComponent
  implements OnInit, AfterContentInit
{
  @ViewChildren(MatSelect) children: QueryList<MatSelect>;
  @ViewChildren('dropdownFake') dropdownFakeContainer: QueryList<ElementRef>;

  public dropdownWidth: number = 0;
  public isDropdownIconOpen: boolean[] = [true, true, true, true, true];
  public preselectedInDropdown: string[] = [null, null, null, null, null];
  public didDropdownDefaultChange: boolean = true;
  public folderSettings: IExportStructure[] = [];
  public fileSettings: IExportStructure[] = [];
  public settingsPartTexts: Record<string, string> = {};
  public alreadyIncludedShortNames: string[] = [];

  protected sessionType: string;
  protected separator: string = '';
  protected specialCharacterReplacement: string = '-';
  protected documentSession: IDocumentSession = null;
  protected readonly originalFilenameDummyData: string = 'ZD240FEN-1209';
  protected readonly serialNumberDummyData: string = 'V12345678';
  protected readonly tagDummyData: string = 'TAGDATA';
  protected readonly emptyCustomerMaterialNumber =
    'No-Customer-material-number';
  protected readonly emptySerialNumber = 'No-Serial-number';

  protected defaultConfiguration$ = this.store.select(defaultConfiguration);
  protected savedConfiguration$ = this.store.select(savedConfiguration);
  protected availableConfiguration$ = this.store.select(availableConfiguration);
  protected documentSession$ = this.store.select(documentSession);
  protected selectedDocumentTypes$ = this.store.select(selectedDocumentTypes);
  protected selectedProducts$ = this.store.select(selectedProducts);
  protected selectedLanguages$ = this.store.select(selectedLanguages);
  private sessionLanguage$ = this.store.select(sessionLanguage);

  public constructor(
    protected store: Store,
    protected trackingHelperService: TrackingHelperService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.documentSession$
      .pipe(takeUntil(this.completeAll$))
      .subscribe((documentSession: IDocumentSession) => {
        this.documentSession = documentSession;
        if (isDefined(() => documentSession.type)) {
          this.sessionType = documentSession.type;
        }
      });

    this.availableConfiguration$
      .pipe(takeUntil(this.completeAll$))
      .subscribe((availableConfiguration: IExportConfiguration) => {
        this.folderSettings = getValue(
          () => availableConfiguration.folderSetting,
          null
        );
        this.fileSettings = getValue(
          () => availableConfiguration.fileSetting,
          null
        );
        this.initStructure();
      });
  }

  public ngAfterContentInit(): void {
    setTimeout(() => {
      if (isDefined(() => this.dropdownFakeContainer)) {
        const firstDropdownFakeContainer: ElementRef =
          this.dropdownFakeContainer.find(
            (select: ElementRef, index: number) => index === 0
          );
        if (isDefined(() => firstDropdownFakeContainer.nativeElement)) {
          this.dropdownWidth =
            getValue(
              () => firstDropdownFakeContainer.nativeElement?.offsetWidth,
              0
            ) + 1;
        }
      }
    }, 0);
  }

  public onReset(): void {}

  public onIconClick(iconClickedIndex: number): void {
    const selectedDropdownSelect: MatSelect = this.children.find(
      (select: MatSelect, index: number) => index === iconClickedIndex
    );
    if (isDefined(() => selectedDropdownSelect.open)) {
      selectedDropdownSelect.open();
    }
  }

  public onOpenedDropdown(iconClickedIndex: number): void {
    this.isDropdownIconOpen[iconClickedIndex] = false;
  }

  public onClosedDropdown(iconClickedIndex: number): void {
    this.isDropdownIconOpen[iconClickedIndex] = true;
  }

  protected initStructure(): void {}

  protected getTextFromStructure(structure: IExportStructure): string {
    let returnValue: string = null;

    if (isDefinedAndFilledArray(() => structure.settingsPart.texts)) {
      const tempText: IExportText = structure.settingsPart.texts.find(
        (text: IExportText) =>
          text.language ===
          this.getLatestValueFromSubscription(this.sessionLanguage$)
      );
      if (isDefinedAndFilledString(() => tempText.label)) {
        returnValue = tempText.label;
      }
    }

    return returnValue;
  }

  protected updateExampleText(settings: IExportStructure[]): string {
    let newExampleText: string = '';
    const selectedDocumentTypes: IDocumentType[] =
      this.getLatestValueFromSubscription(this.selectedDocumentTypes$);
    const selectedProducts: IProductItem[] =
      this.getLatestValueFromSubscription(this.selectedProducts$);
    const selectedLanguages: ILanguageText[] =
      this.getLatestValueFromSubscription(this.selectedLanguages$);

    if (isDefinedAndFilledArray(() => settings)) {
      settings.forEach((setting: IExportStructure) => {
        switch (getValue(() => setting.shortName)) {
          case ExportSettingType.DOCUMENT_TYPE:
            newExampleText = this.addToText(
              newExampleText,
              this.getFirstDocumentTypeText(selectedDocumentTypes)
            );
            break;
          case ExportSettingType.CUSTOM_MATERIAL_NUMBER:
            newExampleText = this.addToText(
              newExampleText,
              this.getFirstCustomerMaterialNr(selectedProducts)
            );
            break;
          case ExportSettingType.LANGUAGE:
            newExampleText = this.addToText(
              newExampleText,
              this.getFirstLanguage(selectedLanguages)
            );
            break;
          case ExportSettingType.ORDER_CODE:
            newExampleText = this.addToText(
              newExampleText,
              this.getFirstOrderCode(selectedProducts)
            );
            break;
          case ExportSettingType.QUOTE_POSITION_NUMBER:
          case ExportSettingType.ORDER_POSITION_NUMBER:
            newExampleText = this.addToText(
              newExampleText,
              this.getFirstPosNumber(selectedProducts)
            );
            break;
          case ExportSettingType.QUOTE_NUMBER:
          case ExportSettingType.ORDER_NUMBER:
            newExampleText = this.addToText(
              newExampleText,
              this.getSessionOrderNumber(this.documentSession)
            );
            break;
          case ExportSettingType.QUOTE_REFERENCE:
          case ExportSettingType.ORDER_REFERENCE:
            newExampleText = this.addToText(
              newExampleText,
              this.getSessionOrderReferenceNumber(this.documentSession)
            );
            break;
          case ExportSettingType.SERIAL_NUMBER:
            newExampleText = this.addToText(
              newExampleText,
              this.getFirstSerialNumber(this.documentSession)
            );
            break;
          case ExportSettingType.TAG:
            newExampleText = this.addToText(newExampleText, this.tagDummyData);
            break;
          case ExportSettingType.ORIGINAL_FILENAME:
            newExampleText = this.addToText(
              newExampleText,
              this.originalFilenameDummyData
            );
            break;
        }
      });
    }

    return newExampleText;
  }

  private addToText(currentText: string, newText: string): string {
    // do the same replacement that is done in the BE
    const regex = /[\\\/:*?\"<>()|_]+/gm;
    const preparedNewText: string = getValue(() => newText, '').replace(
      regex,
      this.specialCharacterReplacement
    );
    let returnValue: string = preparedNewText;

    if (
      isDefinedAndFilledString(() => currentText) &&
      isDefinedAndFilledString(() => newText)
    ) {
      returnValue = currentText + this.separator + preparedNewText;
    } else if (isDefinedAndFilledString(() => currentText)) {
      returnValue = currentText;
    }

    return returnValue;
  }

  private getFirstDocumentTypeText(
    selectedDocumentTypes: IDocumentType[]
  ): string {
    return SOVerify.getValueFromStructure(selectedDocumentTypes, 0, 'text');
  }

  private getFirstCustomerMaterialNr(selectedProducts: IProductItem[]): string {
    let returnValue: string = this.emptyCustomerMaterialNumber;

    if (isDefinedAndFilledArray(() => selectedProducts)) {
      selectedProducts.forEach((product: IProductItem) => {
        const currentMaterialNr: string = getValue(
          () => product.customerMaterialNr,
          null
        );
        if (
          returnValue === this.emptyCustomerMaterialNumber &&
          isDefinedAndFilledString(() => currentMaterialNr)
        ) {
          returnValue = currentMaterialNr;
        }
      });
    }

    return returnValue;
  }

  private getFirstLanguage(selectedLanguages: ILanguageText[]): string {
    return SOVerify.getValueFromStructure(selectedLanguages, 0, 'language');
  }

  private getFirstOrderCode(selectedProducts: IProductItem[]): string {
    return SOVerify.getValueFromStructure(selectedProducts, 0, 'orderCode');
  }

  private getFirstPosNumber(selectedProducts: IProductItem[]): string {
    return SOVerify.getValueFromStructure(selectedProducts, 0, 'posNumber');
  }

  private getSessionOrderNumber(documentSession: IDocumentSession): string {
    return SOVerify.getValueFromStructure(documentSession, 'orderNumber');
  }

  private getSessionOrderReferenceNumber(
    documentSession: IDocumentSession
  ): string {
    return SOVerify.getValueFromStructure(documentSession, 'orderReference');
  }

  private getFirstSerialNumber(documentSession: IDocumentSession): string {
    return isDefined(() => documentSession.produced) && documentSession.produced
      ? this.serialNumberDummyData
      : this.emptySerialNumber;
  }
}
